<template>
  <Container
    :class="$style.userGroupAsidePanelContainer"
  >
    <ValidationObserver ref="membershipObserver">
      <AsidePanel
        ref="asidePanel"
        :components="components"
        :handleMountedCallback="handleMounted"
        :handleSave="handleSave"
        :isExpanded="isExpanded"
        :itemId="itemId"
        :itemType="itemType"
        :navigateToTab="activeTab"
        :overrideBodyNavItems="$style.noOverflow"
        :redirectTo="redirectTo"
        :viewport="viewport"
      >
        <template v-slot:avatar>
          <AsideIcon
            icon="userGroup"
            modifier="windows"
            :showModifier="isManagedByActiveDirectory"
          />
        </template>
      </AsidePanel>
      <!-- Avoid using itemId directly to prevent errors when creating a
        new usergroup, use the id from the item object instead -->
      <MembershipSuggestionsModal
        v-if="enableMembershipSuggestionsModal"
        :handleCancel="membershipSuggestionsModalData.handleCancel"
        :handleContinue="membershipSuggestionsModalData.handleContinue"
        :itemId="item.id"
        itemType="suggestedSystemUser"
        :redirectAfterClosing="itemId === undefined"
        @close="() => toggleMembershipSuggestionsModal(false)"
      />

      <NotificationInPage
        :class="$style.notification"
        data-test-id="NotificationInPage__automationWarning"
        :notification="userAsidePanelNotificationConfig"
        :showNotification="showWarning"
        @close="handleNotificationClose"
      >
        <template
          v-slot:message
        >
          <div :class="$style.notificationMessage">
            {{ notificationSettings.firstBodyPart }}
            <strong>{{ notificationSettings.labelBodyPart }}</strong>
            {{ notificationSettings.secondBodyPart }}
            <Link
              data-test-id="NotificationInPage__addExemptionLink"
              href="#"
              :text="notificationSettings.text"
              @click.native="handleLinkClicked"
            />
          </div>
        </template>
      </NotificationInPage>
      <SeverityModal
        ref="systemGroupBindingChangeModal"
        notificationTitle="This will alter who can log in to your devices."
        :primaryButtonProps="primaryButtonProps"
        :showModal="showDeviceGroupBindingModal"
        :title="deviceGroupButtonTitle"
        variant="sev2"
        @close="handleCancelSystemGroupChange"
        @save="handleConfirmSystemGroupChange"
      >
        <template v-slot:subtitle>
          <span>Are you sure you want to change this user group's assigned device groups?</span>
        </template>
        <template v-slot:body>
          <ParagraphBody>
            <div v-if="showAddDeviceGroupWarning">
              Adding a device group to a user group will create local accounts on <b>all</b>
              devices in the device groups. This may lead to performance issues on the devices.
            </div>
            <br v-if="showAddDeviceGroupWarning && showRemoveDeviceGroupWarning">
            <div v-if="showRemoveDeviceGroupWarning">
              Removing a device group from a user group will cause those users to lose
              <b>all</b> access to these devices.
            </div>
          </ParagraphBody>
        </template>
        <template v-slot:learnmore>
          <ParagraphBody>
            For more information, see
            <Link
              href="https://support.jumpcloud.com/s/article/Binding-Users-to-Devices"
              target="_blank"
              text="Binding Users to Devices"
            />
          </ParagraphBody>
        </template>
      </SeverityModal>
    </ValidationObserver>
  </Container>
</template>

<script>
import {
  Container,
  Link,
  NotificationInPage,
  ParagraphBody,
  SeverityModal,
  Types,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { notify } from 'client/util';
import ApplicationItemTable from 'client/components/ItemTable/ApplicationItemTable.vue';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import DirectoryItemTable from 'client/directories/components/DirectoryItemTable.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemType from 'client/util/Constants/ItemType';
import MembershipSuggestionsModal from 'client/groups/User/MembershipSuggestionsModal.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import NoSuggestionsModal from 'client/groups/User/NoSuggestionsModal.vue';
import RadiusUserGroupWrap from 'client/Radius/RadiusUserGroupWrap.vue';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemGroupItemTable from 'client/components/ItemTable/SystemGroupItemTable.vue';
import UserGroupDetails from 'client/groups/User/UserGroupDetails.vue';
import UserGroupSystemUsers from 'client/groups/User/UserGroupSystemUsers.vue';
import onlyPosixErrors from 'client/util/UserGroupAsideUtil';
import pluralize from 'pluralize';

import Constants, {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';

const { DeviceGroupItemType } = ItemType;
const deviceGroupItem = new DeviceGroupItemType();

const { NotificationConfig, PrimaryButtonProps } = Types;

const { create, update } = ItemActionConsts;
const groupCollection = StoreModuleMap[ItemTypeConsts.group];
const suggestedSystemUsersCollection = StoreModuleMap[ItemTypeConsts.suggestedSystemUser];
const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];
const userGroupModel = StoreModelMap[ItemTypeConsts.userGroup];

export default {
  name: 'UserGroupAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    Container,
    Link,
    MembershipSuggestionsModal,
    NotificationInPage,
    ParagraphBody,
    SeverityModal,
    ValidationObserver,
  },

  mixins: [
    AsidePanelPropsMixin,
    RouteGuardMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.userGroup,
      required: false,
    },
    navigateToTab: {
      type: Object,
      default: () => {},
      validator(obj) {
        if (obj) {
          return typeof obj.tabLabel !== 'undefined';
        }
        return true;
      },
    },
  },

  data() {
    const appItemType = ItemTypeConsts.application;

    const notificationSettings = {
      firstBodyPart: 'Warning: This group has attribute value conditions that do not match this user\'s attributes. Would you like to add',
      labelBodyPart: '',
      secondBodyPart: 'to the group\'s exemption list to prevent a membership override?',
      text: 'Add User To Exemption List',
    };

    const data = {
      activeTab: this.navigateToTab,
      itemHasBeenSaved: false,
      notificationSettings,
      users: {
        action: '',
        ids: [],
        names: '',
      },
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
      redirectTo: ItemTypeRoutes[ItemTypeConsts.userGroup],
      enableMembershipSuggestionsModal: false,
      deviceGroupButtonText: 'Save',
      deviceGroupButtonTitle: 'Change Assigned Device Groups',
      showRemoveDeviceGroupWarning: false,
      showAddDeviceGroupWarning: false,
      showDeviceGroupBindingModal: false,
      showWarning: false,
      systemUserComponentProps: {
        customHeader: true,
        boundItemsUrl: this.itemId
          ? `/api/v2/usergroups/${this.itemId}/membership`
          : undefined,
        handleBoundUsersChanged: (showWarning, users) => this
          .handleBoundUsersChanged(showWarning, users),
        handleReviewSuggestions: () => this.handleReviewSuggestions(),
      },
      // TODO: Add properties for systemGroup, applications, etc so that after creating a new group
      // the bound item URLs for each of these can be updated in turn.
      systemGroupComponentProps: {
        boundItemsUrl: this.itemId
          ? `/api/v2/usergroups/${this.itemId}/systemgroups`
          : undefined,
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.groupAsidePanel,
        parent: this.item,
        showNewWorkflow: true,
      },
      applicationComponentProps: {
        boundItemsUrl: this.itemId
          ? `/api/v2/usergroups/${this.itemId}/applications`
          : undefined,
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.groupAsidePanel,
        showSelectedItemsCount: false,
      },
      radiusComponentProps: {
        boundItemsUrl: this.itemId
          ? `/api/v2/usergroups/${this.itemId}/radiusservers`
          : undefined,
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.groupAsidePanel,
      },
      directoryComponentProps: {
        boundItemsUrl: this.itemId
          ? `/api/v2/usergroups/${this.itemId}/directories`
          : undefined,
        inDetailsAsidePanel: true,
        panelType: ItemTableConsts.panelType.groupAsidePanel,
      },
    };

    data.canBindUserGroupToUsers = true;

    data.components = [
      {
        label: 'Details',
        vueComponent: UserGroupDetails,
      },
      {
        itemType: ItemTypeConsts.systemUser,
        label: pluralize(ItemTypeDisplayNames[ItemTypeConsts.systemUser]),
        vueComponent: UserGroupSystemUsers,
        vueComponentProps: data.systemUserComponentProps,
      },
      {
        itemType: ItemTypeConsts.systemGroup,
        label: deviceGroupItem.titlePlural,
        headerLabels: {
          [create]: `This user group is bound to the following ${deviceGroupItem.labelPlural}`,
          [update]: `user group is bound to the following ${deviceGroupItem.labelPlural}`,
        },
        vueComponent: SystemGroupItemTable,
        vueComponentProps: data.systemGroupComponentProps,
      },
      {
        itemType: appItemType,
        label: 'Applications',
        headerLabels: {
          [create]: 'This user group is bound to the following applications',
          [update]: 'user group is bound to the following applications',
        },
        vueComponent: ApplicationItemTable,
        vueComponentProps: data.applicationComponentProps,
      },
      {
        itemType: ItemTypeConsts.radiusServer,
        label: 'RADIUS',
        vueComponent: RadiusUserGroupWrap,
        vueComponentProps: data.radiusComponentProps,
      },
      {
        itemType: ItemTypeConsts.directory,
        label: 'Directories',
        headerLabels: {
          [create]: 'This user group has the following directories enabled',
          [update]: 'user group has the following directories enabled',
        },
        renderImmediately: true,
        vueComponent: DirectoryItemTable,
        vueComponentProps: data.directoryComponentProps,
      },
    ];

    data.hideUserGroupSavedNotification = false;

    data.membershipSuggestionsModalData = {
      handleCancel: () => {
        const payload = {
          action: 'Close Membership Suggestions Confirmation Dialog',
          type: 'Membership Suggestions',
        };
        EventHelper.triggerMembershipSuggestionsModalClosed(payload);
      },

      handleContinue: async (params = {}) => {
        const payload = {
          action: 'Accept and Save',
          type: 'Membership Suggestions',
        };
        EventHelper.triggerMembershipSuggestionsButtonClicked(payload);
        try {
          const canContinue = true;

          this.canBindUserGroupToUsers = false;

          // This code is needed to avoid 'create user group' api call when a new
          // user group is created but not saved in the aside panel immediately
          // UPDATE: we need to verify first if item is being created so we close
          // the aside panel or not
          const groupBeingCreated = this.isItemBeingCreated();
          if (groupBeingCreated) this.setItemStateToUpdate();

          await this.handleSave(canContinue).then(async () => {
            notify(Constants.success, `${ItemTypeDisplayNames[this.itemType]} saved successfully!`);
            this.canBindUserGroupToUsers = true;

            if (params) {
              const usersAddedMsg = this.getNotificationText(params.usersAdded, 'added');
              const usersRemovedMsg = this.getNotificationText(params.usersRemoved, 'removed');
              notify(Constants.success, `${usersAddedMsg} and ${usersRemovedMsg}.`);

              // Updating System User Bound Items Url
              this.systemUserComponentProps.boundItemsUrl = `/api/v2/usergroups/${this.item.id}/membership`;
              // Updating System Group Bound Items Url
              this.systemGroupComponentProps.boundItemsUrl = `/api/v2/usergroups/${this.item.id}/systemgroups`;
              // Updating Applications Bound Items Url
              this.applicationComponentProps.boundItemsUrl = `/api/v2/usergroups/${this.item.id}/applications`;
              // Updating Radius Bound Items Url
              this.radiusComponentProps.boundItemsUrl = `/api/v2/usergroups/${this.item.id}/radiusservers`;
              // Updating Directories Bound Items Url
              this.directoryComponentProps.boundItemsUrl = `/api/v2/usergroups/${this.item.id}/directories`;

              this.resetAndFetchSystemUserCollection();
            }
          });

          // Reset on success so that dialog will be displayed if another change is made
          this.itemHasBeenSaved = false;
        } catch (e) {
          notify(Constants.error, `Unable to save user group! ${e.toString()}`);
        }
      },
    };

    return data;
  },

  computed: {
    ...mapGetters(userGroupModel, [
      'isManagedByActiveDirectory',
      'getUserAttributes',
      'getRadiusReplyAttributes',
    ]),

    ...mapState(userGroupModel, {
      hasPosixGroup: state => state.item.hasPosixGroup,
      item: state => state.item,
    }),

    onlyPosixErrors() {
      // TODO: Check item.scope === 'PosixGroup' once MFA-605 and MFA-606 are resolved. Currently
      // the <InputValidator> adds the v-validate directive to the <InputOptionalValidation>
      // component but the data-vv-scope is a child of that component, hence the scope isn't
      // available. So for now, the field name is checked instead.
      // SEE: https://jumpcloud.atlassian.net/browse/MFA-605

      return onlyPosixErrors(this.v2Errors.items);
    },

    primaryButtonProps() {
      return PrimaryButtonProps({
        isLoading: false,
        isDisabled: false,
        hasOutline: false,
        text: this.deviceGroupButtonText,
        type: 'primary',
      });
    },

    userAsidePanelNotificationConfig() {
      return NotificationConfig({
        icon: 'error',
        title: 'Membership Suggestion Conflict',
        type: 'warning',
        isMarkdown: false,
        showCloseButton: true,
      });
    },
  },

  beforeMount() {
    this.$data.systemGroupComponentProps.parent = this.item;
  },

  methods: {
    ...mapActions(userGroupModel, [
      'bindSudoStatus',
      'bindUserGroupToUsers',
      'bindUserGroupToSystemGroups',
      'bindUserGroupToApplications',
      'bindUserGroupToRadiusServers',
      'bindUserGroupToDirectories',
      'saveItem',
      'setItemField',
      'setItemStateToUpdate',
    ]),

    ...mapActions(groupCollection, [
      'refreshCollection',
    ]),

    ...mapActions(suggestedSystemUsersCollection, {
      fetchCollection: 'fetchCollection',
      setFetchSuggestionsTimer: 'setFetchSuggestionsTimer',
      setUserGroupId: 'setUserGroupId',
    }),

    ...mapActions(systemUserCollection, {
      fetchSystemUserCollection: 'fetchCollection',
      resetSystemUserCollection: 'resetCollection',
      setSystemUserCollectionBoundItemsUrl: 'setBoundItemsUrl',
    }),

    ...mapGetters(userGroupModel, [
      'hasItemChanged',
      'haveAssociationsChanged',
      'getSystemGroupAssociationCounts',
      'isItemBeingCreated',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    displayConfirmationDialog(messageConfig) {
      return new Promise((resolve) => {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            mdTemplate: messageConfig.mdTemplate,
            cancelText: messageConfig.cancelText,
            continueText: messageConfig.continueText,
            enableMainAction: true,
            handleCancel: () => {
              ModalEventBus.close();
              resolve(false);
            },
            handleContinue: () => {
              ModalEventBus.close();
              resolve(true);
            },
            headerText: messageConfig.headerText,
            showClose: true,
          },
          preventCloseOnOverlayClick: true,
        });
      });
    },

    getNotificationText(count, action) {
      return `${count} ${pluralize('user', count, false)} ${pluralize('was', count, false)} ${action}`;
    },

    handleBoundUsersChanged(showWarning = false, users = { action: '', ids: [], names: '' }) {
      this.showWarning = showWarning;
      this.notificationSettings.labelBodyPart = users.names;
      this.users = users;
    },

    handleCancelSystemGroupChange() {
      this.showDeviceGroupBindingModal = false;
    },

    async handleConfirmSystemGroupChange() {
      try {
        await this.saveAndBindUserGroup();
        this.triggerSuccessNotification({
          message: 'User Group saved successfully!',
        });
        this.$refs.asidePanel.redirect();
      } catch (error) {
        let message = '**Unable to save User Group!**';

        if (error && error.details && error.details.length > 0) {
          message = error.details[0].fieldViolationsList[0].field.indexOf('radius') > -1
            ? 'There was an issue validating RADIUS Reply Attributes on the RADIUS tab' : message;
        }

        if (error && error.message && !error.details) {
          message = `${message}\n${error.message}`;
        }

        this.triggerErrorNotification({ isMarkdown: true, message });
      } finally {
        this.showDeviceGroupBindingModal = false;
      }
    },

    handleLinkClicked() {
      const { ids } = this.users;
      const key = 'memberQueryExemptions';

      const newExemptions = ids.map((id) => ({ id, type: 'USER' }));

      const value = [...this.item.memberQueryExemptions, ...newExemptions];
      this.showWarning = false;
      this.setItemField({ key, value });
    },

    handleMounted() {
      if (this.navigateToTab) {
        this.navigateToAsideTab(this.navigateToTab.tabLabel);
      }
    },

    handleNotificationClose() {
      this.showWarning = !this.showWarning;
    },

    async handleReviewSuggestions() {
      let showReviewSuggestionsDialog = true;

      if (this.hasModelChanged()) {
        const confirmationDialogParams = {
          mdTemplate: 'Save your changes before you review suggested membership updates. Do you want to continue?',
          cancelText: 'cancel',
          continueText: 'save and continue',
          headerText: 'Save Changes',
        };
        showReviewSuggestionsDialog = await
        this.displayConfirmationDialog(confirmationDialogParams);

        if (showReviewSuggestionsDialog) {
          try {
            await this.handleSave(false);
            this.resetAndFetchSystemUserCollection();

            notify(Constants.success, `${ItemTypeDisplayNames[this.itemType]} saved successfully!`);

            const payload = {
              action: 'Confirm changes and save',
              type: 'Membership Suggestions',
            };
            EventHelper.triggerMembershipSuggestionsButtonClicked(payload);

            this.setFetchSuggestionsTimer(10000);
            // Reset on success so that dialog will be displayed if another change is made
            this.itemHasBeenSaved = false;

            this.setItemStateToUpdate();
          } catch (e) {
            showReviewSuggestionsDialog = false;
            notify(Constants.error, `Unable to save user group! ${e.toString()}`);
          }
        }
      } else {
        this.setFetchSuggestionsTimer(0);
      }

      if (!showReviewSuggestionsDialog) return;

      try {
        const { id } = this.item;
        this.setUserGroupId(id);
        this.toggleMembershipSuggestionsModal(true);
      } catch (e) {
        const message = `Unable to fetch user suggestions ${e.toString()}`;
        notify(Constants.error, message);
      }
    },

    async handleSave(canContinue = true) {
      await this.$refs.membershipObserver.validate();
      const { flags } = this.$refs.membershipObserver;
      if (this.item.membershipAutomated && flags.changed) {
        const confirmationDialogParams = {
          mdTemplate: 'Click Save to update the attribute value conditions. You may see immediate membership changes to this user group.\n\nAre you sure you want to make these changes?',
          cancelText: 'Cancel',
          continueText: 'Save',
          headerText: 'Confirm Group Attribute Updates',
        };
        const confirmationDialogResult = await
        this.displayConfirmationDialog(confirmationDialogParams);
        if (!confirmationDialogResult) {
          return new SaveReturnValue({
            canContinue: false,
            noNotification: true,
          });
        }
      }

      const isV3FormSuccessful = flags.validated && flags.valid;

      if (!isV3FormSuccessful) {
        throw Error('Enter all required fields in user membership suggestions to save.');
      }

      await this.$validator.validate();

      if (this.$validator.errors.any()) {
        if (this.hasUserAttributesErrors()) {
          throw Error('An error occurred while trying to save custom attributes.');
        }

        if (this.hasRadiusReplyAttributesErrors()) {
          throw Error('An error occurred while trying to save Radius Reply Attributes.');
        }

        // Ignore the validation error if there are only Posix errors and the admin has chosen
        // not to create a Posix group.
        if (!this.onlyPosixErrors || (this.onlyPosixErrors && this.hasPosixGroup)) {
          throw Error(this.validationErrorMessage);
        }
      }

      // `handleSystemGroupAssociationChange` will check if device group associations have changed
      const hasSystemGroupsChanged = this.handleSystemGroupAssociationChange();
      if (!hasSystemGroupsChanged) {
        await this.saveAndBindUserGroup();
      }

      // if the system groups have changed
      // a severity modal with buttons will display to let the user confirm/cancel the changes
      return new SaveReturnValue({
        canContinue: hasSystemGroupsChanged ? false : canContinue,
        noNotification: hasSystemGroupsChanged,
      });
    },

    handleSystemGroupAssociationChange() {
      const { bind, unbind } = this.getSystemGroupAssociationCounts();
      this.showDeviceGroupBindingModal = (bind || unbind) !== 0;
      this.showAddDeviceGroupWarning = bind !== 0;
      this.showRemoveDeviceGroupWarning = unbind !== 0;
      this.updateDeviceGroupModalText();
      return this.showDeviceGroupBindingModal;
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.hasItemChanged() || this.haveAssociationsChanged();
    },

    hasRadiusReplyAttributesErrors() {
      const radiusReplyAttributes = this.getRadiusReplyAttributes || [];

      return radiusReplyAttributes.some(radiusReplyAttribute => this.v2Errors.has(`attribute-${radiusReplyAttribute.tempId}-name`)
        || this.v2Errors.has(`attribute-${radiusReplyAttribute.tempId}-value`));
    },

    hasUserAttributesErrors() {
      const userAttributes = this.getUserAttributes || [];

      return userAttributes.some(userAttribute => this.v2Errors.has(`attribute-${userAttribute.tempId}-name`)
        || this.v2Errors.has(`attribute-${userAttribute.tempId}-value`));
    },

    navigateToAsideTab(tabLabel) {
      this.activeTab = { tabLabel };
    },

    openNoSuggestionsModal() {
      this.toggleMembershipSuggestionsModal(false);

      ModalEventBus.open({
        component: NoSuggestionsModal,
      });
    },

    resetAndFetchSystemUserCollection() {
      this.resetSystemUserCollection();
      this.setSystemUserCollectionBoundItemsUrl({
        url: this.systemUserComponentProps.boundItemsUrl,
      });

      this.fetchSystemUserCollection();
    },

    async saveAndBindUserGroup() {
      try {
        const item = await this.saveItem();
        // bind entities
        if (this.canBindUserGroupToUsers) await this.bindUserGroupToUsers();
        await this.bindUserGroupToSystemGroups();
        await this.bindUserGroupToApplications(ItemTypeConsts.application);
        await this.bindUserGroupToRadiusServers();
        await this.bindUserGroupToDirectories();
        await this.bindSudoStatus();

        await this.refreshCollection({ item });

        this.itemHasBeenSaved = true;
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }
        let message = '**Unable to save User Group!**';

        if (error && error.details && error.details.length > 0) {
          message = error.details[0].fieldViolationsList[0].field.indexOf('radius') > -1
            ? 'There was an issue validating RADIUS Reply Attributes on the RADIUS tab' : message;
        }

        if (error && error.message && !error.details) {
          message = `${message}\n${error.message}`;
        }
        throw Error(message);
      }
    },

    toggleMembershipSuggestionsModal(show) {
      this.enableMembershipSuggestionsModal = show;
    },

    updateDeviceGroupModalText() {
      if (this.showAddDeviceGroupWarning && !this.showRemoveDeviceGroupWarning) {
        this.deviceGroupButtonText = 'Bind Device Groups';
        this.deviceGroupButtonTitle = 'Bind Device Group to User Group';
      }
      if (this.showRemoveDeviceGroupWarning && !this.showAddDeviceGroupWarning) {
        this.deviceGroupButtonText = 'Unbind Device Groups';
        this.deviceGroupButtonTitle = 'Unbind Device Group from User Groups';
      }
      if (this.showAddDeviceGroupWarning && this.showRemoveDeviceGroupWarning) {
        this.deviceGroupButtonText = 'Confirm';
        this.deviceGroupButtonTitle = 'Change Assigned Device Groups';
      }
    },
  },

};
</script>

<style module>
.userGroupAsidePanelContainer {
  height: 100%;

  /*
    position & z-index to fix UI Acceptance tests, as this element
    was manifesting below the page, causing many tests to fail
    as Cypress deemed the elements within "not visible" because
    this element was outside the overflow of its parent.
  */
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 20;
}

:global([data-test-id="NotificationInPage__addExemptionLink"]) {
  margin-top: 1.063rem;
}

.userGroupAsidePanelContainer :global([data-test-id="NotificationInPage__automationWarning"]) {
  margin: 0 auto 1rem;

  /* need to force override on this because there are issues with other components */
  max-width: calc(52% - 25rem) !important;
  position: absolute;
  right: 2.125rem;
  width: 100%;
  z-index: 1001;
}

/* CSS Override to show announcement */
.noOverflow {
  overflow-x: unset !important;
}

.notificationMessage {
  display: flex;
  flex-direction: column;
}
</style>
